if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', () => {
              // JSOC: product-info.js leaves the gallery alone for the colour change this click causes (callouts 13/15).
              this.pendingThumbnailMediaId = mediaToSwitch.dataset.target;
              this.setActiveMedia(mediaToSwitch.dataset.target, false);
              this.selectColor(mediaToSwitch.dataset.target);
            });
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      selectColor(mediaId) {
        // JSOC: clicking a colour's photo selects that colour. Dawn 16 removed the old
        // variant-data accessor, and its picker JSON carries only the selected variant, so the
        // data comes from the JSOC-owned [data-j-variants] tag rendered by the variant picker.
        try {
          const section = this.closest('section');
          const picker = section && section.querySelector('variant-selects');
          const store = section && section.querySelector('[data-j-variants]');
          if (!picker || !store) return;
          const variants = JSON.parse(store.textContent || '[]');
          const variant = variants.find((v) => v.featured_media && mediaId.endsWith('-' + v.featured_media.id));
          if (!variant) return;
          picker.querySelectorAll('fieldset').forEach((group, index) => {
            if (!group.querySelector('input[name*="Color"], input[name*="Colour"]')) return;
            const input = [...group.querySelectorAll('input')].find((i) => i.value === variant.options[index]);
            // Dispatch on the INPUT, not the host: v16 reads event.target to resolve the change.
            if (input && !input.checked) {
              input.checked = true;
              input.dispatchEvent(new Event('change', { bubbles: true }));
            }
          });
        } catch (_) { /* a gallery click must never break the page */ }
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            const scrollOptions = { left: activeMedia.offsetLeft };
            // JSOC mobile: after a variant reorders slides, Firefox can retain the
            // previous snap position while CSS smooth scrolling is in flight.
            // Align the selected image immediately; retain normal thumbnail motion.
            const jsocProductIds = ['15393826537701', '8202265592037', '8202266050789', '8289172652261', '8289177305317', '8289177829605', '8289178452197', '8289179369701', '8308776763621', '8308777418981', '8308777484517', '8320993132773', '8320996770021', '8320997228773', '9787347468517'];
            if (prepend && !this.mql.matches && jsocProductIds.includes(this.closest('product-info')?.dataset.productId)) {
              scrollOptions.behavior = 'instant';
            }
            activeMedia.parentElement.scrollTo(scrollOptions);
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
